import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Panel, ListGrid, GhostButton } from "./ui";
import {
  formatKurus,
  DP,
  LOW_BALANCE_KURUS,
  transactionTypeLabel,
  TRANSACTION_TYPE,
} from "./constants";
import { formatEpoch } from "../../utils/dateUtils";
import { getPersonal, getTransactionsByFilter } from "../../api/digitalPayAPI";

function Stat({ label, value, tone = "primary", icon }) {
  const color = DP[tone] || DP.primary;
  return (
    <Box
      sx={{
        bgcolor: DP.surface,
        border: `1px solid ${DP.border}`,
        borderRadius: DP.radius,
        boxShadow: DP.shadowSm,
        px: 2.5,
        py: 2.25,
        position: "relative",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 3,
          bgcolor: color,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography
            sx={{
              color: DP.textMuted,
              fontSize: "0.74rem",
              fontWeight: 650,
              letterSpacing: "0.025em",
              mb: 0.6,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              color,
              fontSize: "1.6rem",
              fontWeight: 750,
              lineHeight: 1.15,
              letterSpacing: "-0.035em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            bgcolor: color + "14",
            color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </Box>
  );
}

export default function DashboardPanel() {
  const [people, setPeople] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [todayTx, setTodayTx] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const startOfDay = Math.floor(
        new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime() / 1000
      );
      const endOfDay = Math.floor(
        new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).getTime() / 1000
      );
      const [p, t, today] = await Promise.all([
        getPersonal(),
        getTransactionsByFilter({ count: 10 }),
        getTransactionsByFilter({ startEpoch: startOfDay, endEpoch: endOfDay, count: 0 }),
      ]);
      setPeople(p);
      setTransactions(t);
      setTodayTx(today);
    } catch {
      // Panel boş kalır; hata detayı ilgili ekranlarda gösteriliyor.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const lowCards = people.filter((p) => (p.cardBalance || 0) < LOW_BALANCE_KURUS).length;
    // Günlük ödeme = bugünkü PAYMENT tipli işlemler.
    const paymentsToday = todayTx.filter(
      (tx) => Number(tx.transactionTypeId) === TRANSACTION_TYPE.PAYMENT
    );
    const dailyPaymentCount = paymentsToday.length;
    const dailyPaymentAmount = Math.abs(
      paymentsToday.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0)
    );
    return { lowCards, dailyPaymentCount, dailyPaymentAmount };
  }, [people, todayTx]);

  const recent = useMemo(
    () => [...transactions].sort((a, b) => (b.epoch || 0) - (a.epoch || 0)).slice(0, 10),
    [transactions]
  );

  const columns = [
    {
      field: "epoch",
      headerName: "Tarih",
      flex: 1,
      minWidth: 150,
      valueFormatter: (v) => formatEpoch(v),
    },
    { field: "nameSurname", headerName: "Personel", flex: 1, minWidth: 140 },
    { field: "departmentName", headerName: "Departman", flex: 1, minWidth: 120 },
    { field: "productName", headerName: "Ürün", flex: 1, minWidth: 120 },
    {
      field: "transactionTypeId",
      headerName: "İşlem Tipi",
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => transactionTypeLabel(value),
    },
    {
      field: "amount",
      headerName: "Tutar",
      width: 110,
      valueFormatter: (v) => formatKurus(v),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            xl: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Stat label="Toplam Kart" value={people.length} icon={<PeopleAltRoundedIcon />} />
        <Stat
          label="Düşük Bakiyeli Kart"
          value={stats.lowCards}
          tone="danger"
          icon={<WarningAmberRoundedIcon />}
        />
        <Stat
          label="Günlük Toplam Ödeme İşlemi Tutarı"
          value={formatKurus(stats.dailyPaymentAmount)}
          tone="success"
          icon={<PaidRoundedIcon />}
        />
        <Stat
          label="Günlük Toplam Ödeme İşlemi Adedi"
          value={stats.dailyPaymentCount}
          tone="primary"
          icon={<ReceiptLongRoundedIcon />}
        />
      </Box>

      <Panel
        title="Son İşlemler"
        sub="En son 10 hareket"
        action={
          <GhostButton startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Yenile
          </GhostButton>
        }
      >
        <ListGrid
          rows={recent}
          columns={columns}
          getRowId={(r) => r.idtransaction}
          loading={loading}
          autoHeight
          hideFooter
        />
      </Panel>
    </>
  );
}
